<script lang="ts">
  import { onMount } from "svelte";
  import { searchSelectionService } from "state/SelectionService";
  import {
    pageState,
    searchParams,
    setActiveSearchResultSource,
    setSearchScrollPosition,
    showDetails,
    showDetailsNtb,
  } from "state/page";
  import { getCountFromQuery } from "state/queries";
  import { createPreloader, getMimirSearchHits, getMimirSearchQuery } from "state/queries/mimir";
  import { showCount } from "utils/fns";

  import { upload } from "components/Upload/upload";
  import { debounce } from "lodash-es";
  import ResultsWrapper from "./ResultsWrapper.svelte";
  import ViewSettings from "./ViewSettings.svelte";
  import { itemOnKeydown } from "./keyboardInteraction";
  import { createBildebankenSearchHitHandler, createNtbSearchHitHandler } from "./searchHitHandler";
  import { getNtbSearchHits, getNtbSearchQuery } from "state/queries/ntb";
  import { useFeatureToggle } from "services/configCatService";
  import { orgFilter } from "@nrk/origo";
  import { tabFor } from "bildebanken-model/dist/es/type-util";

  export let filterOpen: boolean = false;
  export let toggleFilter: (() => void) | undefined = undefined;

  let bbTabs: HTMLElement & {
    get tab(): HTMLElement;
    set tab(tab: number | string);
    tabs: any;
    panel: any;
    panels: any;
  };
  let wrapperEl: HTMLElement;

  const mimirQuery = getMimirSearchQuery();
  const ntbQuery = getNtbSearchQuery();
  const mimirSearchHits = getMimirSearchHits();
  const ntbSearchHits = getNtbSearchHits();
  const mimirCount = getCountFromQuery(mimirQuery);
  const ntbCount = getCountFromQuery(ntbQuery);

  let dragOver = false;

  function selectActiveTab(mimirCount: number, ntbCount: number, mq: typeof $mimirQuery) {
    if (bbTabs) {
      if (mq.isFetchingNextPage || $ntbQuery.isFetchingNextPage || mq.isFetching) {
        return;
      }
      const tabOrder = [
        { id: "bildebanken-tab", count: mimirCount },
        { id: "ntb-tab", count: ntbCount },
      ];

      const currentTab = tabOrder.find((tab) => tab.id === bbTabs.tab?.id);

      if (currentTab && currentTab.count === 0) {
        const nextTab = tabOrder.find((tab) => tab.count > 0);
        if (nextTab) {
          bbTabs.tab = nextTab.id;
        } else bbTabs.tab = "bildebanken-tab";
      }
    }
  }

  $: selectActiveTab($mimirCount, $ntbCount, $mimirQuery);

  function tabsToggled() {
    let currentTab;
    if (bbTabs && bbTabs.tab.id === "ntb-tab") {
      currentTab = "Ntb";
    } else {
      currentTab = "Bildebanken";
    }
    setActiveSearchResultSource(currentTab);
  }

  $: activeSearchSource = $pageState.type === "SEARCH" && $pageState.activeSearchResultSource;
  $: selectionService = searchSelectionService;

  $: activeSearchHitIds = () => {
    if (activeSearchSource === "Ntb") {
      return $ntbSearchHits.map((hit) => hit.id);
    }
    return $mimirSearchHits.map((hit) => hit.id);
  };

  $: selectionService.send({
    type: "UPDATE_ITEMS",
    itemIds: activeSearchHitIds(),
  });

  function switchTabToPreviousSearchResultsTab() {
    if (bbTabs && activeSearchSource) {
      switch (activeSearchSource) {
        case "Bildebanken":
          bbTabs.tab = "bildebanken-tab";
          break;
        case "Ntb":
          bbTabs.tab = "ntb-tab";
          break;
        default:
          bbTabs.tab = "bildebanken-tab";
          break;
      }
      tabsToggled();
    }
  }

  // Focus first image selector on arrow right
  function focusOnArrowRight(event: KeyboardEvent & { target: Element }) {
    if (event.code !== "ArrowRight") return;
    if (event.target.nodeName === "INPUT" || event.target.nodeName === "TEXTAREA") return;

    document.querySelector<HTMLInputElement>("input[name=select-image]")?.focus();
  }

  function scrollToPreviousScrollState() {
    if (wrapperEl && "searchScrollPosition" in $pageState) {
      requestAnimationFrame(() => {
        if (!wrapperEl) return;
        if ($pageState.type === "SEARCH") {
          wrapperEl.scrollTo({
            top: $pageState.searchScrollPosition || 0,
            behavior: "instant",
          });
        }
      });
    }
  }

  function onScroll(this: HTMLElement) {
    updateScroll(this.scrollTop);
  }

  const updateScroll = debounce(setSearchScrollPosition, 100);

  onMount(() => {
    document.addEventListener("keydown", focusOnArrowRight);
    document.addEventListener("tabs-toggle", tabsToggled);
    setTimeout(() => {
      switchTabToPreviousSearchResultsTab();
      scrollToPreviousScrollState();
    }, 0);

    return () => {
      document.removeEventListener("keydown", focusOnArrowRight);
      document.removeEventListener("tabs-toggle", tabsToggled);
    };
  });
  let windowWidth;
  const breakPoint = 768;
  $: isMobile = windowWidth <= breakPoint;
</script>

<svelte:window bind:innerWidth={windowWidth} />
<!-- svelte-ignore a11y-no-static-element-interactions-->
<div
  class="wrapper"
  data-testid="search-results"
  bind:this={wrapperEl}
  class:dragOver
  on:dragover|preventDefault={(event) => {
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "copy";
      event.dataTransfer.dropEffect = "copy";
    }
    dragOver = true;
  }}
  on:dragleave|preventDefault={(_) => {
    dragOver = false;
  }}
  on:drop|preventDefault={(event) => {
    dragOver = false;
    if (event.dataTransfer) {
      upload(event.dataTransfer.files);
    }
  }}
  on:scroll={onScroll}
>
  <h1 class="screen-reader-only">Søkeresultater</h1>
  <div class="results-settings-bar">
    <div class="order2">
      <ViewSettings />
    </div>
    <div class="tabs-with-filter order1">
      {#if toggleFilter && !filterOpen}
        <button
          type="button"
          aria-label="Filtrer resultater"
          data-testid="open-filter"
          class="org-button filter-button org-button--secondary"
          title="Filtrer resultater"
          on:click={toggleFilter}
        >
          {@html orgFilter}
        </button>
      {/if}
      <bb-tabs bind:this={bbTabs}>
        <button id="bildebanken-tab" {...tabFor("bildebanken")}>
          {#if isMobile}
            <span class="tab-button-label" aria-label="Bildebanken">Bb</span>
          {:else}
            <span class="tab-button-label">Bildebanken</span>
          {/if}
          <span class="counter-wrapper">
            <span>
              {#if $mimirQuery.isSuccess}
                <span title="{$mimirCount.toLocaleString('no-nb')} bilder"
                  >{`(${showCount($mimirCount || 0)})`}</span
                >
              {:else}
                (…)
              {/if}
            </span>
          </span>
        </button>
        {#if !$searchParams.filter.folderId}
          <button id="ntb-tab" {...tabFor("ntb")}>
            <span class="tab-button-label">NTB</span>
            <span class="counter-wrapper">
              <span>
                {#if $ntbQuery.isSuccess}
                  <span title="{$ntbCount.toLocaleString('no-nb')} bilder"
                    >{`(${showCount($ntbCount || 0)})`}</span
                  >
                {:else}
                  (…)
                {/if}
              </span>
            </span></button
          >
        {/if}
      </bb-tabs>
    </div>
  </div>

  <div class="tab" id="bildebanken" data-testid="bildebanken-search-results">
    <ResultsWrapper
      query={mimirQuery}
      searchHits={$mimirSearchHits}
      handler={createBildebankenSearchHitHandler(selectionService)}
      {selectionService}
      onKeyDown={itemOnKeydown({
        selectionService,
        openDetailsForItem: showDetails,
      })}
      {createPreloader}
    />
  </div>
  <div class="tab" id="ntb" data-testid="ntb-search-results" hidden>
    <ResultsWrapper
      query={ntbQuery}
      searchHits={$ntbSearchHits}
      handler={createNtbSearchHitHandler(selectionService)}
      {selectionService}
      onKeyDown={itemOnKeydown({
        selectionService,
        openDetailsForItem: showDetailsNtb,
      })}
    />
  </div>
</div>

<style>
  .results-settings-bar {
    display: flex;
    position: sticky;
    top: 0px;
    z-index: 2;
    background-color: var(--color-surface-primary);
    border-radius: 5px;
    justify-content: space-between;
    box-shadow: 0px 5px 10px 5px var(--color-surface-primary);
    padding-top: var(--org-xsmall);
  }

  .order1 {
    order: 1;
  }

  .tabs-with-filter {
    display: flex;
    align-items: center;
    gap: var(--org-xsmall);
  }

  .filter-button {
    height: 2rem;
    align-self: flex-start;
    background: transparent;
  }

  .order2 {
    order: 2;
  }

  .screen-reader-only {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    position: absolute;
    white-space: nowrap;
    overflow: hidden;
    width: 1px;
  }

  button {
    color: var(--color-text-secondary);
    width: fit-content;
  }

  bb-tabs,
  .tab {
    background: transparent;
  }

  bb-tabs button {
    padding-left: var(--org-small);
    padding-right: var(--org-small);
    gap: var(--org-xsmall);
  }

  .tab-button-label {
    display: inline-block;
  }

  .counter-wrapper {
    font-weight: normal;
    font-size: 12px;
    display: inline-block;
    text-align: left;
  }

  .wrapper {
    flex: 1;
    max-height: 100%;
    overflow: auto;
    border-radius: 5px;
    padding: 0px var(--org-xsmall) var(--org-xsmall) var(--org-xsmall);
    display: flex;
    flex-direction: column;
    gap: var(--org-small);
    color: var(--color-text-primary);
  }

  .dragOver {
    padding: 2px;
    padding-top: 3px;
    border: 3px solid #3d81d8;
    background: #0c366c;
  }

  @media screen and (max-width: 480px) {
    .counter-wrapper {
      display: none;
    }
  }
</style>
